import { useEffect, useState } from 'react';
import moment from 'moment';

// In-memory progress of uploads running in this tab, keyed by upload_id. The server only learns
// about finished files later, so the Upload Progress view overlays these live numbers on its rows.
let uploads = {};
const listeners = new Set();

export const LIVE_STATUS = { UPLOADING: 'UPLOADING', PAUSED: 'PAUSED', DONE: 'DONE' };

// Closing or reloading the tab stops every upload running in it, so the browser asks first while any
// is in progress. (Browsers show their own generic message; custom text is ignored.)
const warnBeforeUnload = (evt) => {
  evt.preventDefault();
  evt.returnValue = '';
};
let unloadWarningOn = false;
const syncUnloadWarning = () => {
  // A paused run is lost on close too, so it counts
  const uploading = Object.values(uploads).some(upload => upload.status === LIVE_STATUS.UPLOADING || upload.status === LIVE_STATUS.PAUSED);
  if (uploading === unloadWarningOn) return;
  unloadWarningOn = uploading;
  if (uploading) window.addEventListener('beforeunload', warnBeforeUnload);
  else window.removeEventListener('beforeunload', warnBeforeUnload);
};

export const updateLiveUpload = (uploadId, data) => {
  uploads = { ...uploads, [uploadId]: { ...uploads[uploadId], ...data, uploadId } };
  syncUnloadWarning();
  listeners.forEach(listener => listener(uploads));
};

export const useLiveUploads = () => {
  const [state, setState] = useState(uploads);
  useEffect(() => {
    // Catch up on anything published between the first render and subscribing
    setState(uploads);
    listeners.add(setState);
    return () => listeners.delete(setState);
  }, []);
  return state;
};

// Uploads in other tabs of this browser: the uploading tab holds a Web Lock per upload_id for as long
// as the engine runs, and the browser releases it when that tab finishes, closes or crashes. Other
// tabs read the held locks to tell "uploading elsewhere" from "interrupted", and taking the lock
// before uploading stops two tabs from uploading the same session at once.
// Uploads from another browser, device or user are detected from server timestamps instead
// (isRecentActivity below).
const LOCK_PREFIX = 'stream-upload:';
const locksSupported = () => typeof navigator !== 'undefined' && !!navigator.locks;

// Runs task() while holding the session's lock; resolves onBusy() instead if another tab holds it
export const withUploadLock = (uploadId, task, onBusy) => {
  if (!locksSupported()) return task();
  return navigator.locks.request(LOCK_PREFIX + uploadId, { ifAvailable: true }, lock => (lock ? task() : onBusy()));
};

// Resolves the upload_ids being uploaded by any tab of this browser (including this one)
export const getLockedUploadIds = () => {
  if (!locksSupported()) return Promise.resolve(new Set());
  return navigator.locks.query()
    .then(({ held = [] }) => new Set(held.map(lock => lock.name).filter(name => name && name.startsWith(LOCK_PREFIX)).map(name => name.slice(LOCK_PREFIX.length))))
    .catch(() => new Set());
};

// A running upload reports each file's progress every 30 s (and the session updates whenever a file
// finishes), so activity within this window means someone is still uploading, on any tab, device or
// user. Measured with this computer's clock against the server's timestamps.
const ACTIVITY_WINDOW_MINUTES = 2;
// Unfinished file statuses: files that a running upload may still be working on
export const UNFINISHED_FILE_STATUSES = ['PENDING', 'UPLOADING'];

export const isRecentActivity = (timestamp) => {
  // moment(undefined) is "now", which would count a missing timestamp as activity forever
  if (!timestamp) return false;
  const at = moment(timestamp);
  return at.isValid() && moment().diff(at, 'minutes', true) < ACTIVITY_WINDOW_MINUTES;
};

// The browser's own online/offline signal. It catches the device losing its network (and DevTools'
// Offline mode), not every outage: a network with no internet still reports online, so retries remain
// the fallback for those.
const isOnline = () => typeof navigator === 'undefined' || navigator.onLine !== false;

// Resolves once the browser is online again (immediately if it already is)
export const waitForOnline = () => (isOnline() ? Promise.resolve() : new Promise(resolve => {
  window.addEventListener('online', () => resolve(), { once: true });
}));

export const useOnlineStatus = () => {
  const [online, setOnline] = useState(isOnline);
  useEffect(() => {
    const update = () => setOnline(isOnline());
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);
  return online;
};
